/**
 * Live Remember Us ✦ — iter211 PROJECTOR
 * ─────────────────────────────────────────────────────────────
 * Fullscreen projection surface for the venue. Atmosphere, not
 * software. No counters, no status, no timestamps, no user lists,
 * no diagnostics. The scribbles ARE the motion.
 *
 * Architectural contract (Craig, iter211):
 *
 *   • Wall state stays vector. Projection renderer scales to the
 *     viewport (NOT wall-native resolution), with a DPR cap of 2
 *     so old kiosk GPUs don't choke when walls get dense.
 *   • SSE + short-poll fallback via useRememberUsStream — delta-
 *     only `?since=` so the server NEVER replays the whole wall
 *     on reconnect.
 *   • Newest-stroke glow lifecycle:
 *       - logarithmic decay over ~45s
 *       - first ~8s visibly alive
 *       - next ~20s subtle presence
 *       - final ~17s nearly imperceptible
 *     Glow ONLY after the stroke lands (never during draw — the
 *     guest's own client is what's drawing, the projector is the
 *     atmosphere downstream).
 *   • Ambient drift only after ~20s wall-inactivity. Max ±1.5%
 *     translation. Almost imperceptible. The wall remembers itself.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import useRememberUsStream from '../hooks/useRememberUsStream';
import { resolveTheme, materialVariance, strokeColor as themeStrokeColor } from '../lib/booth-os/wallThemes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WALL_WIDTH = 1080;
const WALL_HEIGHT = 1920;

// Glow lifecycle constants (Craig, iter211).
const GLOW_TOTAL_MS = 45_000;
const GLOW_BRIGHT_PHASE_MS = 8_000;   // visibly alive
const GLOW_SUBTLE_PHASE_MS = 20_000;  // subtle presence (8s → 28s)
// Remaining ~17s is the nearly-imperceptible tail (28s → 45s).

// Ambient drift constants.
const DRIFT_IDLE_TRIGGER_MS = 20_000;
const DRIFT_AMP_PCT = 0.015;   // ±1.5% translation max
const DRIFT_SPEED_HZ = 0.04;   // very slow

/**
 * Logarithmic glow curve. Returns 0..1 intensity for the given
 * milliseconds since the stroke landed.
 *
 *   t < 8s         → bright (1.0 → 0.55, log curve)
 *   8s ≤ t < 28s   → subtle (0.55 → 0.18, log curve)
 *   28s ≤ t < 45s  → trace  (0.18 → 0.0,  log curve)
 *   t ≥ 45s        → 0
 */
function glowIntensity(ageMs) {
  if (ageMs <= 0) return 1.0;
  if (ageMs >= GLOW_TOTAL_MS) return 0;
  if (ageMs < GLOW_BRIGHT_PHASE_MS) {
    const p = ageMs / GLOW_BRIGHT_PHASE_MS;
    return 1.0 - 0.45 * Math.log1p(p * (Math.E - 1)) / 1;
  }
  if (ageMs < GLOW_BRIGHT_PHASE_MS + GLOW_SUBTLE_PHASE_MS) {
    const p = (ageMs - GLOW_BRIGHT_PHASE_MS) / GLOW_SUBTLE_PHASE_MS;
    return 0.55 - 0.37 * Math.log1p(p * (Math.E - 1)) / 1;
  }
  const tailMs = GLOW_TOTAL_MS - (GLOW_BRIGHT_PHASE_MS + GLOW_SUBTLE_PHASE_MS);
  const p = (ageMs - GLOW_BRIGHT_PHASE_MS - GLOW_SUBTLE_PHASE_MS) / tailMs;
  return 0.18 * (1 - Math.log1p(p * (Math.E - 1)) / 1);
}

/**
 * iter212 — replayMode contract:
 *
 *   Same component. Same renderer pipeline. Same glow lifecycle.
 *   Same material variance. Same drift restraint.
 *
 *   In replayMode the projector:
 *     • Starts with an EMPTY ledger.
 *     • Subscribes to /replay-stream instead of /stream.
 *     • Feeds every chunked stroke through the SAME onStroke
 *       handler — so glow + density + chalk dust + drift all
 *       behave identically to a live evening.
 *     • Fires onReplayProgress({delivered, total, complete})
 *       so a parent surface (WallArchaeologyPage) can render an
 *       archival, interaction-revealed timeline.
 *
 *   Anything that diverges between live and replay breaks Craig's
 *   "the wall remembering itself" contract — so we deliberately
 *   share, never fork.
 */
/**
 * iter195b — Empty-wall atmospheric placeholder.
 *
 * Belief 2 + 5: a Leavers Night room before the first mark should
 * feel like the room before everyone arrives — patient,
 * anticipatory — never like a component with no data. This overlay
 * is consumed by LiveRememberUsPage and rendered inside the same
 * letterboxed wall container as the ledger canvas so it inherits
 * the projector's spatial frame exactly.
 *
 * Visual contract (Craig, iter195b):
 *   • Faint chalk haze radial gradient (centre lift ~6%)
 *   • Barely-visible wall framing marks (corner ticks)
 *   • Single line of editorial italic copy, low contrast
 *   • NO animations except the imperceptible ambient drift the
 *     projector wrapper already applies to the whole stage
 *   • NO onboarding UI, NO arrows, NO prompts
 *   • Dissolves on first stroke with a long, soft opacity fade
 */
const WallPlaceholder = ({ mode, visible }) => {
  // `visible` toggles the opacity transition. We keep the element
  // mounted briefly after dismissal so the fade completes — once
  // mode flips to 'dismissed' the parent unmounts us.
  const copy = mode === 'renewal'
    ? 'A new wall has begun ✦'
    : 'The wall will begin with the first mark ✦';
  return (
    <div
      data-testid="wall-placeholder"
      data-mode={mode}
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        // Long, soft dissolve. No celebration, no pop.
        transition: 'opacity 1600ms ease-out',
        // Faint chalk haze — barely there. Sits inside the
        // letterboxed wall container so it shares the projector's
        // boxShadow inset.
        background:
          'radial-gradient(ellipse at center, ' +
          'rgba(248,232,200,0.022) 0%, ' +
          'rgba(248,232,200,0.012) 35%, ' +
          'rgba(0,0,0,0) 72%)',
      }}
    >
      {/* Corner framing ticks — ultra-low-opacity, no animation. */}
      {[
        { top: 14, left: 14, borderTop: '1px solid', borderLeft: '1px solid' },
        { top: 14, right: 14, borderTop: '1px solid', borderRight: '1px solid' },
        { bottom: 14, left: 14, borderBottom: '1px solid', borderLeft: '1px solid' },
        { bottom: 14, right: 14, borderBottom: '1px solid', borderRight: '1px solid' },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 22, height: 22,
            borderColor: 'rgba(248,232,200,0.08)',
            ...pos,
          }}
        />
      ))}
      <p
        data-testid="wall-placeholder-copy"
        style={{
          margin: 0,
          fontFamily: '"Georgia", "Cormorant Garamond", serif',
          fontStyle: 'italic',
          fontSize: 'clamp(11px, 1.2vw, 13px)',
          letterSpacing: '0.05em',
          // Low contrast — readable in the dark, never demanding.
          color: 'rgba(248,232,200,0.32)',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          userSelect: 'none',
        }}
      >
        {copy}
      </p>
    </div>
  );
};


const LiveRememberUsPage = ({
  replayMode = false,
  replaySpeedMul = 50,
  onReplayProgress = null,
} = {}) => {
  const { slug } = useParams();
  const [bootDone, setBootDone] = useState(false);
  const [boothMode, setBoothMode] = useState('wedding_room');
  // eslint-disable-next-line no-unused-vars
  const [wallDensity, setWallDensity] = useState(0);
  const theme = useMemo(() => resolveTheme(boothMode), [boothMode]);
  const [error, setError] = useState('');

  // iter195b — Pre-first-mark placeholder. Atmosphere, not UI.
  //
  // Three states:
  //   'idle'      — wall has never been written to. Shows
  //                 "The wall will begin with the first mark ✦"
  //   'renewal'   — admin cleared the wall (onEpoch). Shows
  //                 "A new wall has begun ✦"
  //   'dismissed' — a mark has landed in the current epoch.
  //                 Placeholder is permanently gone until either
  //                 a re-mount or an epoch reset.
  //
  // The dismissed state is one-way within an epoch — SSE
  // reconnects, density recomputation, or boot retries MUST NOT
  // bring it back. We keep an explicit ref so the placeholder
  // can't flicker even under aggressive React state churn.
  const [placeholderMode, setPlaceholderMode] = useState('idle');
  const placeholderEverArmedRef = useRef(false);  // sticky-dismissal guard

  // Two-layer renderer mirrors the drawing page — the LEDGER carries
  // the wall, the GLOW layer overlays recent strokes with their
  // logarithmic decay. (The glow layer is redrawn every frame but
  // the ledger is only painted on stroke arrival.)
  const containerRef = useRef(null);
  const ledgerRef = useRef(null);
  const glowRef = useRef(null);
  const ledgerCtxRef = useRef(null);
  const glowCtxRef = useRef(null);
  const cachedStrokesRef = useRef([]);
  const recentStrokesRef = useRef([]);    // newest first; pruned when intensity hits 0
  const lastStrokeAtRef = useRef(0);      // for ambient-drift trigger
  const mappingRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });
  const animRef = useRef(0);
  const driftPhaseRef = useRef(0);

  // ── Wall → screen mapping ─────────────────────────────────
  const wallToScreen = useCallback((x, y) => {
    const m = mappingRef.current;
    return { x: m.offsetX + x * m.scale, y: m.offsetY + y * m.scale };
  }, []);

  const drawStrokeOnto = useCallback((ctx, stroke, alpha = 1) => {
    if (!stroke?.points?.length) return;
    const material = stroke.stroke_style || theme.material;
    const variance = materialVariance(stroke, material);
    const baseColor = stroke.color || theme.defaultColor;
    const finalColor = themeStrokeColor(stroke, baseColor, material);
    ctx.save();
    ctx.globalAlpha = Math.min(1, variance.alpha * alpha);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = finalColor;
    const w = (stroke.width || theme.defaultWidth) * variance.widthMul;
    const m = mappingRef.current;
    if (stroke.points.length === 1) {
      const [x, y] = stroke.points[0];
      const s = wallToScreen(x, y);
      ctx.fillStyle = finalColor;
      ctx.beginPath();
      ctx.arc(s.x, s.y, (w * m.scale) / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    ctx.beginPath();
    const first = wallToScreen(stroke.points[0][0], stroke.points[0][1]);
    ctx.moveTo(first.x, first.y);
    // iter198 — Quadratic-midpoint smoothing on the projector
    // wall too. The participant's slate (RememberUsPage) uses the
    // same algorithm so what the year group draws on their phone
    // and what the hall sees on the projector stay visually
    // identical. Continuous chalk/marker flow, not segmented
    // touch logging. See RememberUsPage.drawStrokeOnto for the
    // full rationale.
    const pts = stroke.points;
    let pressureSum = 0;
    let pressureN = 0;
    for (let i = 1; i < pts.length; i += 1) {
      const b = wallToScreen(pts[i][0], pts[i][1]);
      if (i < pts.length - 1) {
        const c = wallToScreen(pts[i + 1][0], pts[i + 1][1]);
        const mx = (b.x + c.x) / 2;
        const my = (b.y + c.y) / 2;
        ctx.quadraticCurveTo(b.x, b.y, mx, my);
      } else {
        ctx.lineTo(b.x, b.y);
      }
      const p = pts[i][3];
      if (p != null) { pressureSum += p; pressureN += 1; }
    }
    const avgPressure = pressureN > 0 ? pressureSum / pressureN : 0.7;
    ctx.lineWidth = Math.max(0.5, w * m.scale * (0.7 + avgPressure * 0.5));
    ctx.stroke();
    if (material === 'chalk' && variance.dust > 0) {
      ctx.globalAlpha = Math.min(1, variance.alpha * variance.dust * alpha);
      ctx.lineWidth = Math.max(1, w * m.scale * 1.8);
      ctx.stroke();
    }
    ctx.restore();
  }, [theme, wallToScreen]);

  const repaintLedger = useCallback(() => {
    const c = ledgerRef.current;
    const ctx = ledgerCtxRef.current;
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    for (const s of cachedStrokesRef.current) drawStrokeOnto(ctx, s);
  }, [drawStrokeOnto]);

  const resizeCanvases = useCallback(() => {
    const wrap = containerRef.current;
    const ledger = ledgerRef.current;
    const glow = glowRef.current;
    if (!wrap || !ledger || !glow) return;
    const rect = wrap.getBoundingClientRect();
    // DPR capped at 2 — full native 3x DPI on dense walls melts
    // kiosk GPUs. Projection smoothness > perfect fidelity.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = rect.width;
    const cssH = rect.height;
    ledger.width = Math.max(2, Math.round(cssW * dpr));
    ledger.height = Math.max(2, Math.round(cssH * dpr));
    glow.width = ledger.width;
    glow.height = ledger.height;
    ledger.style.width = `${cssW}px`;
    ledger.style.height = `${cssH}px`;
    glow.style.width = `${cssW}px`;
    glow.style.height = `${cssH}px`;
    // Letterbox the wall coordinate space into the projection.
    const scaleX = ledger.width / WALL_WIDTH;
    const scaleY = ledger.height / WALL_HEIGHT;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (ledger.width - WALL_WIDTH * scale) / 2;
    const offsetY = (ledger.height - WALL_HEIGHT * scale) / 2;
    mappingRef.current = { scale, offsetX, offsetY };
    repaintLedger();
  }, [repaintLedger]);

  // ── Boot: hydrate the wall ────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    let aborted = false;
    (async () => {
      try {
        // Always read meta so we know boothMode + wall_density —
        // replayMode starts with an empty ledger but still wears
        // the right theme.
        const metaP = axios.get(`${API}/remember-us/events/${slug}/meta`);
        const strokesP = replayMode
          ? Promise.resolve({ data: { strokes: [], booth_mode: null } })
          : axios.get(`${API}/remember-us/events/${slug}/strokes`);
        const [m, s] = await Promise.all([metaP, strokesP]);
        if (aborted) return;
        const mode = m.data?.booth_mode || s.data?.booth_mode || 'wedding_room';
        setBoothMode(mode);
        setWallDensity(typeof m.data?.wall_density === 'number' ? m.data.wall_density : 0);
        const strokes = replayMode ? [] : (s.data?.strokes || []);
        cachedStrokesRef.current = strokes;
        // iter195b — if the wall already has marks at boot, the
        // placeholder has nothing to wait for. Dismiss immediately
        // and arm the sticky guard so SSE reconnects can't summon
        // it back. Replay mode skips this entirely (the wall fills
        // rapidly under its own pacing).
        if (replayMode || strokes.length > 0) {
          placeholderEverArmedRef.current = true;
          setPlaceholderMode('dismissed');
        }
        setBootDone(true);
        requestAnimationFrame(() => resizeCanvases());
      } catch (e) {
        if (!aborted) {
          setError('Couldn’t reach the wall server.');
          setBootDone(true);
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug, resizeCanvases, replayMode]);

  useEffect(() => {
    ledgerCtxRef.current = ledgerRef.current?.getContext('2d') || null;
    glowCtxRef.current = glowRef.current?.getContext('2d') || null;
  }, []);

  // ── Resize ────────────────────────────────────────────────
  useEffect(() => {
    if (!bootDone) return undefined;
    const onResize = () => resizeCanvases();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bootDone, resizeCanvases]);

  // ── Live stream subscription ──────────────────────────────
  // In replayMode we disable the live stream and let the replay-
  // stream effect below feed strokes through the SAME handler.
  const handleNewStroke = useCallback((stroke) => {
    cachedStrokesRef.current.push(stroke);
    drawStrokeOnto(ledgerCtxRef.current, stroke);
    recentStrokesRef.current.unshift({ ...stroke, _arrivedAt: performance.now() });
    lastStrokeAtRef.current = performance.now();
    const now = performance.now();
    recentStrokesRef.current = recentStrokesRef.current
      .filter((r) => now - r._arrivedAt < GLOW_TOTAL_MS);
    // iter195b — first mark of the epoch dismisses the placeholder
    // permanently (until either a re-mount or an explicit epoch
    // reset). We arm the sticky guard so the placeholder can't
    // sneak back in on SSE reconnects or boot retries.
    if (!placeholderEverArmedRef.current) {
      placeholderEverArmedRef.current = true;
      setPlaceholderMode('dismissed');
    }
  }, [drawStrokeOnto]);

  const stream = useRememberUsStream({
    apiBase: BACKEND_URL,
    slug,
    enabled: bootDone && !replayMode,
    onStroke: handleNewStroke,
    onEpoch: useCallback(() => {
      // iter214 admin clear — reset everything. Same render path.
      cachedStrokesRef.current = [];
      recentStrokesRef.current = [];
      repaintLedger();
      // iter195b — epoch reset re-arms the placeholder, but with
      // renewal copy ("A new wall has begun ✦") instead of the
      // initial-state copy. This distinguishes anticipation from
      // emergence from renewal — generic empty-state behaviour is
      // explicitly avoided.
      placeholderEverArmedRef.current = false;
      setPlaceholderMode('renewal');
    }, [repaintLedger]),
  });

  // Seed the stream's seen-id set + since-anchor with the boot
  // payload so we never replay history through the SSE channel.
  useEffect(() => {
    if (!bootDone || replayMode) return;
    const strokes = cachedStrokesRef.current;
    stream.seedSeen(strokes.map((s) => s.stroke_id));
    if (strokes.length) {
      const lastAt = strokes[strokes.length - 1].created_at;
      stream.seedSince(lastAt);
    }
  }, [bootDone, stream, replayMode]);

  // ── iter212 — Replay-stream subscription (archaeology mode) ─
  // The wall remembers itself. Strokes arrive through the SAME
  // handleNewStroke path the live projector uses — preserving
  // glow lifecycle, chalk dust, drift restraint, and density-
  // sensitive theming. No separate renderer. Ever.
  //
  // The server already paces chunks via `chunk_delay_ms`. We pace
  // INSIDE each chunk on the client so strokes don't all land in
  // the same frame — that's what makes "the wall reconstructing
  // itself" feel honest. Per-stroke interval scales with
  // replaySpeedMul.
  useEffect(() => {
    if (!bootDone || !replayMode || !slug) return undefined;
    if (typeof window === 'undefined' || typeof window.EventSource !== 'function') return undefined;

    // ~50x feel: ~20 strokes/sec → 50ms inter-stroke at 1x event-time.
    // replaySpeedMul=50 → 1ms inter-stroke pacing. Cap at 0.5ms so
    // the browser still has breath to render each stroke before the
    // next one queues.
    const interStrokeMs = Math.max(0.5, 50 / Math.max(1, replaySpeedMul));
    const url = `${API}/remember-us/events/${slug}/replay-stream?chunk_size=25&chunk_delay_ms=40`;
    let es;
    let cancelled = false;
    let delivered = 0;
    let total = 0;

    const reportProgress = (extras = {}) => {
      try {
        onReplayProgress?.({ delivered, total, ...extras });
      } catch { /* parent crash never breaks the renderer */ }
    };

    // Pre-flight: get the total expected count so the parent can
    // map progress to "early evening → midnight → final moments".
    axios.get(`${API}/remember-us/events/${slug}/meta`)
      .then((m) => { total = m.data?.total || 0; reportProgress(); })
      .catch(() => { /* total stays 0 — progress bar just absent */ });

    try {
      es = new window.EventSource(url);
    } catch {
      return undefined;
    }

    const pendingTimers = [];

    const feedStroke = (s, delayMs) => new Promise((resolve) => {
      const t = setTimeout(() => {
        if (cancelled) { resolve(); return; }
        handleNewStroke(s);
        delivered += 1;
        if (delivered % 10 === 0 || delivered === total) reportProgress();
        resolve();
      }, delayMs);
      pendingTimers.push(t);
    });

    let chunkQueue = Promise.resolve();
    es.addEventListener('chunk', (ev) => {
      try {
        const batch = JSON.parse(ev.data);
        if (!Array.isArray(batch)) return;
        chunkQueue = chunkQueue.then(async () => {
          for (let i = 0; i < batch.length; i += 1) {
            if (cancelled) return;
            await feedStroke(batch[i], i === 0 ? 0 : interStrokeMs);
          }
        });
      } catch { /* malformed chunk — skip */ }
    });
    es.addEventListener('complete', () => {
      chunkQueue.then(() => {
        if (cancelled) return;
        reportProgress({ complete: true });
      });
    });
    es.addEventListener('error', () => {
      // Replay can run to completion BEFORE error fires (server
      // closes the stream after `complete`). We don't surface this
      // visually — it's archival, not editable.
      try { es.close(); } catch { /* ignore */ }
    });

    return () => {
      cancelled = true;
      try { es.close(); } catch { /* ignore */ }
      for (const t of pendingTimers) clearTimeout(t);
      // Reset the ledger so a re-mount of the page starts clean.
      cachedStrokesRef.current = [];
      recentStrokesRef.current = [];
      repaintLedger();
    };
  }, [bootDone, replayMode, slug, replaySpeedMul, handleNewStroke, repaintLedger, onReplayProgress]);

  // ── Animation loop: glow decay + ambient drift ────────────
  useEffect(() => {
    if (!bootDone) return undefined;
    const tick = () => {
      const glowCtx = glowCtxRef.current;
      const ledger = ledgerRef.current;
      if (!glowCtx || !ledger) {
        animRef.current = requestAnimationFrame(tick);
        return;
      }
      glowCtx.clearRect(0, 0, ledger.width, ledger.height);
      const now = performance.now();
      // Prune fully-decayed strokes.
      recentStrokesRef.current = recentStrokesRef.current
        .filter((r) => now - r._arrivedAt < GLOW_TOTAL_MS);
      // Paint each recent stroke with its intensity. We draw with a
      // very soft additive blend so dense overlaps glow brighter,
      // matching real ink/chalk emotional behaviour.
      glowCtx.save();
      glowCtx.globalCompositeOperation = 'lighter';
      for (const r of recentStrokesRef.current) {
        const age = now - r._arrivedAt;
        const intensity = glowIntensity(age);
        if (intensity <= 0.01) continue;
        drawStrokeOnto(glowCtx, r, Math.min(1, intensity * 0.85));
      }
      glowCtx.restore();
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [bootDone, drawStrokeOnto]);

  // ── Ambient drift — restraint above all ───────────────────
  // Activates only after DRIFT_IDLE_TRIGGER_MS of no new strokes.
  // Pure CSS transform on the wall wrapper — never compositionally
  // expensive. Decays back to 0 instantly when a new mark lands.
  const wallWrapperRef = useRef(null);
  useEffect(() => {
    if (!bootDone) return undefined;
    const tick = () => {
      const wrapper = wallWrapperRef.current;
      if (!wrapper) {
        driftPhaseRef.current = requestAnimationFrame(tick);
        return;
      }
      const now = performance.now();
      const idleMs = now - lastStrokeAtRef.current;
      if (idleMs > DRIFT_IDLE_TRIGGER_MS && lastStrokeAtRef.current > 0) {
        const phaseT = now * DRIFT_SPEED_HZ * 0.001;
        const tx = Math.sin(phaseT) * DRIFT_AMP_PCT;
        const ty = Math.cos(phaseT * 0.73) * DRIFT_AMP_PCT;
        wrapper.style.transform = `translate(${tx * 100}%, ${ty * 100}%)`;
      } else {
        wrapper.style.transform = 'translate(0, 0)';
      }
      driftPhaseRef.current = requestAnimationFrame(tick);
    };
    driftPhaseRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(driftPhaseRef.current);
  }, [bootDone]);

  // ── Render ────────────────────────────────────────────────
  // Projector mode is INTENTIONALLY chrome-free. No counters, no
  // QR, no status, no diagnostics. Atmosphere, not software.
  return (
    <div
      data-testid={replayMode ? 'wall-archaeology-page' : 'live-remember-us-page'}
      data-mode={replayMode ? 'replay' : 'live'}
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        background: 'radial-gradient(ellipse at center, #1a1611 0%, #0b0907 75%, #050402 100%)',
        cursor: 'none', /* hide cursor on projection screens */
      }}
    >
      <div
        ref={wallWrapperRef}
        style={{
          position: 'absolute', inset: 0,
          transition: 'transform 1.2s ease-in-out',
          willChange: 'transform',
        }}
      >
        <div
          ref={containerRef}
          style={{ position: 'absolute', inset: '4vh 4vw' }}
        >
          <div style={{
            position: 'relative', width: '100%', height: '100%',
            boxShadow: 'inset 0 0 120px rgba(0,0,0,0.6)',
            borderRadius: 2,
          }}>
            <canvas
              ref={ledgerRef}
              data-testid="live-ledger-canvas"
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            />
            <canvas
              ref={glowRef}
              data-testid="live-glow-canvas"
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none', mixBlendMode: 'screen' }}
            />
            {/* iter195b — atmospheric placeholder. Sits ABOVE the
                canvases (so it's visible on a virgin wall) but
                fades to opacity 0 the instant the first mark
                lands. Kept mounted so the 1.6s dissolve has time
                to play; never unmounted within the same epoch.
                Skipped entirely in replay mode — the wall fills
                under its own pacing there. */}
            {!replayMode && (
              <WallPlaceholder
                mode={placeholderMode === 'dismissed' ? 'idle' : placeholderMode}
                visible={bootDone && placeholderMode !== 'dismissed'}
              />
            )}
          </div>
        </div>
      </div>
      {error && (
        // Even errors stay minimal — no banner, no overlay. A
        // tiny barely-visible eyebrow in the corner the operator
        // can spot, but a guest never reads.
        <p data-testid="live-error" style={{
          position: 'absolute', bottom: 12, right: 16,
          margin: 0, fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: 10, color: 'rgba(248,232,200,0.18)',
          letterSpacing: '0.06em',
        }}>· offline ·</p>
      )}
    </div>
  );
};

export default LiveRememberUsPage;
